/**
 * dom.ts — minimal DOM construction + a reactive binder.
 *
 * The binder lets the render loop update only the nodes whose backing value
 * actually changed (diff against last-rendered), avoiding per-frame DOM churn.
 */

type Child = Node | string | null | undefined | false;

interface ElProps {
  class?: string;
  id?: string;
  text?: string;
  html?: string;
  title?: string;
  type?: string;
  value?: string;
  placeholder?: string;
  disabled?: boolean;
  dataset?: Record<string, string>;
  /** Arbitrary attributes (e.g. ARIA roles/labels). */
  attrs?: Record<string, string>;
  style?: Partial<CSSStyleDeclaration>;
  onClick?: (e: MouseEvent) => void;
  onInput?: (e: Event) => void;
  onChange?: (e: Event) => void;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: ElProps = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.class) node.className = props.class;
  if (props.id) node.id = props.id;
  if (props.text !== undefined) node.textContent = props.text;
  if (props.html !== undefined) node.innerHTML = props.html;
  if (props.title) node.title = props.title;
  if (props.type) (node as HTMLInputElement).type = props.type;
  if (props.value !== undefined) (node as HTMLInputElement).value = props.value;
  if (props.placeholder) (node as HTMLInputElement).placeholder = props.placeholder;
  if (props.disabled !== undefined) (node as HTMLButtonElement).disabled = props.disabled;
  if (props.dataset) for (const [k, v] of Object.entries(props.dataset)) node.dataset[k] = v;
  if (props.attrs) for (const [k, v] of Object.entries(props.attrs)) node.setAttribute(k, v);
  if (props.style) Object.assign(node.style, props.style);
  if (props.onClick) node.addEventListener('click', props.onClick as EventListener);
  if (props.onInput) node.addEventListener('input', props.onInput as EventListener);
  if (props.onChange) node.addEventListener('change', props.onChange as EventListener);
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return node;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

/** A collection of update functions that each diff their value before touching the DOM. */
export class Binder {
  private updaters: Array<() => void> = [];

  /** Register a raw updater (the single chokepoint all bindings route through). */
  protected add(fn: () => void): void {
    this.updaters.push(fn);
  }

  /** Bind an element's textContent to a value getter. */
  text(node: HTMLElement, get: () => string): void {
    let last: string | null = null;
    this.add(() => {
      const v = get();
      if (v !== last) {
        node.textContent = v;
        last = v;
      }
    });
  }

  /**
   * Bind text to a numeric value that animates (count-up) toward the real value.
   * Large jumps (e.g. a prestige reset) snap instantly; normal changes ease.
   */
  number(node: HTMLElement, get: () => number, fmt: (n: number) => string, speed = 0.16): void {
    let shown = get();
    this.add(() => {
      const target = get();
      const diff = target - shown;
      const a = Math.abs(diff);
      if (a < 1e-6) shown = target;
      else if (!Number.isFinite(diff) || a > Math.max(1e4, Math.abs(target) * 3)) shown = target;
      else shown += diff * speed;
      node.textContent = fmt(shown);
    });
  }

  /** Bind an element's class to a boolean (toggles `cls`). */
  toggle(node: HTMLElement, cls: string, get: () => boolean): void {
    let last: boolean | null = null;
    this.add(() => {
      const v = get();
      if (v !== last) {
        node.classList.toggle(cls, v);
        last = v;
      }
    });
  }

  /** Bind a button's disabled state. */
  disabled(node: HTMLButtonElement, get: () => boolean): void {
    let last: boolean | null = null;
    this.add(() => {
      const v = get();
      if (v !== last) {
        node.disabled = v;
        last = v;
      }
    });
  }

  /** Bind an arbitrary side effect that runs each frame (use sparingly). */
  effect(fn: () => void): void {
    this.add(fn);
  }

  /**
   * Return a facade with the same binding API whose registrations only run when
   * `visible()` is true — used to skip hidden tab panels each frame (perf).
   * All updaters still live in this (parent) Binder, guarded by the predicate.
   */
  gate(visible: () => boolean): Binder {
    const parent = this;
    const facade: Binder = Object.create(Binder.prototype);
    (facade as unknown as { add: (fn: () => void) => void }).add = (fn: () => void) => {
      parent.add(() => { if (visible()) fn(); });
    };
    return facade;
  }

  update(): void {
    for (const u of this.updaters) u();
  }

  clear(): void {
    this.updaters = [];
  }
}
